import * as React from "react";

import { cn } from "@/lib/utils";

type PageHeaderProps = Omit<React.ComponentProps<"header">, "title"> & {
  /** Title case. */
  title: React.ReactNode;
  /** Rendered as "· 22" in `fg-3` after the title. */
  count?: number | string | null;
  /** One sentence, 12px `fg-3`. */
  description?: React.ReactNode;
  actions?: React.ReactNode;
};

/** The page title row: 20px title, muted count, a short description, actions right. */
function PageHeader({
  title,
  count,
  description,
  actions,
  className,
  ...props
}: PageHeaderProps) {
  return (
    <header
      data-slot="page-header"
      className={cn(
        "flex min-w-0 items-start justify-between gap-4",
        className
      )}
      {...props}
    >
      <div className="flex min-w-0 flex-col gap-1">
        <h1 className="flex min-w-0 items-baseline gap-1.5 text-xl font-medium text-fg">
          <span className="truncate">{title}</span>
          {count !== null && count !== undefined ? (
            <span className="shrink-0 font-normal text-fg-3 tabular-nums">
              · {count}
            </span>
          ) : null}
        </h1>
        {description ? (
          <p className="text-sm text-fg-3">{description}</p>
        ) : null}
      </div>
      {actions ? (
        <div className="flex shrink-0 items-center gap-1.5">{actions}</div>
      ) : null}
    </header>
  );
}

type SectionHeaderProps = Omit<React.ComponentProps<"div">, "title"> & {
  title: React.ReactNode;
  description?: React.ReactNode;
  actions?: React.ReactNode;
};

/**
 * A 13px weight-500 section label that sits 12px above its card (the parent
 * uses `gap-3`). Title case, never uppercase.
 */
function SectionHeader({
  title,
  description,
  actions,
  className,
  ...props
}: SectionHeaderProps) {
  return (
    <div
      data-slot="section-header"
      className={cn("flex min-w-0 items-end justify-between gap-3", className)}
      {...props}
    >
      <div className="flex min-w-0 flex-col gap-0.5">
        <h2 className="truncate text-base font-medium text-fg">{title}</h2>
        {description ? (
          <p className="text-sm text-fg-3">{description}</p>
        ) : null}
      </div>
      {actions ? (
        <div className="flex shrink-0 items-center gap-1.5">{actions}</div>
      ) : null}
    </div>
  );
}

export { PageHeader, SectionHeader };
export type { PageHeaderProps, SectionHeaderProps };
